import { fingerHand, type Hand, type HandState, type Key, type Tables } from 'ErgoBot';
import type { HandLine, HandLines } from './handLines';
import type { NoteFingering } from './noteFingering';

/** Where each hand was left; null for a hand that has not played yet. */
export type HandStates = Record<Hand, HandState | null>;

export const NO_HAND_STATES: HandStates = { rh: null, lh: null };

/** The time in milliseconds, as `performance.now` gives it. */
export type Clock = () => number;

export interface PhraseFingering {
  /** The finger number and need score of every fingered note, by drawn note. */
  fingeringByNoteId: Map<string, NoteFingering>;
  /** Where the phrase leaves each hand: the states the next phrase starts from. */
  states: HandStates;
  /** The milliseconds the runtime took to finger the phrase's hands. Only when
   *  a clock was given and the runtime was called. */
  runtimeMilliseconds?: number;
}

interface HandFingering {
  fingerings: Array<[string, NoteFingering]>;
  state: HandState | null;
  /** The milliseconds the runtime call took, when it was timed. */
  milliseconds?: number;
}

/** No numbers, and the next phrase places the hand afresh. */
const NOT_FINGERED: HandFingering = { fingerings: [], state: null };

/** What a call returns and, when a clock is given, the milliseconds it took. */
const timed = <Result>(
  clock: Clock | undefined,
  call: () => Result,
): [Result, number | undefined] => {
  if (clock === undefined) return [call(), undefined];
  const started = clock();
  const result = call();
  return [result, clock() - started];
};

const fingerHandLine = (
  tables: Tables,
  tier: string,
  hand: Hand,
  handLine: HandLine,
  state: HandState | null,
  key: Key,
  clock: Clock | undefined,
): HandFingering => {
  // Only the runtime call is timed: reading the line and placing the numbers
  // on the notes are the app's own work.
  const [result, milliseconds] = timed(clock, () =>
    fingerHand(tables, tier, hand, { line: handLine.line, key, state }),
  );
  if (!result.ok) return { ...NOT_FINGERED, milliseconds };
  return {
    fingerings: result.fingers.flatMap((finger, note): Array<[string, NoteFingering]> => {
      const noteId = handLine.noteIds[note];
      return noteId === undefined ? [] : [[noteId, { finger, need: result.need[note] }]];
    }),
    state: result.state,
    milliseconds,
  };
};

/**
 * The finger numbers of one phrase in the key it sounds in, each hand
 * continuing from where the phrase before it left that hand. A hand the tier
 * cannot finger (no tier, a staff that is not one line, notes outside the
 * hand) gets no numbers and no state. With a clock, the result also says how
 * long the runtime took over the hands it fingered.
 */
export const fingerPhraseNotes = (
  tables: Tables,
  tier: string | null,
  lines: HandLines,
  states: HandStates,
  key: Key,
  clock?: Clock,
): PhraseFingering => {
  const fingerOf = (hand: Hand): HandFingering => {
    const handLine = lines[hand];
    return tier === null || handLine === null
      ? NOT_FINGERED
      : fingerHandLine(tables, tier, hand, handLine, states[hand], key, clock);
  };
  const right = fingerOf('rh');
  const left = fingerOf('lh');
  const timings = [right.milliseconds, left.milliseconds].filter(
    (milliseconds): milliseconds is number => milliseconds !== undefined,
  );
  return {
    fingeringByNoteId: new Map([...right.fingerings, ...left.fingerings]),
    states: { rh: right.state, lh: left.state },
    ...(timings.length > 0
      ? { runtimeMilliseconds: timings.reduce((sum, milliseconds) => sum + milliseconds, 0) }
      : {}),
  };
};
